import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { loginService, signupService } from "../../apis/auth";
import { LoginFormInputs } from "../../pages/auth/auth_types";
import { toast } from "react-toastify";
import { User } from "../../pages/private/admin-management/types";
import { Tokens } from "./types/authTypes";

export const acceptedRoles = [
  "COURIER_ADMIN",
  "COURIER_AGENT",
  "OPP_ADMIN",
  "SUPER_ADMIN",
  "DISPUTE_ADMIN",
  "INVESTOR",
];

interface AuthState {
  user: {
    access_token: string;
    refreshToken: string;
    user: User;
  } | null;
  isAuthenticated: boolean;
  authenticatingUser: boolean;
  error: string | null;
  unAuthorizedForgotPassword: {
    email: string;
    hash: string;
    otp: string;
  };
}

const initialState: AuthState = {
  user: JSON.parse(localStorage.getItem("user") ?? "{}") ?? null,
  isAuthenticated: !!localStorage.getItem("user"),
  authenticatingUser: false,
  error: null,
  unAuthorizedForgotPassword: {
    email: "",
    hash: "",
    otp: "",
  },
};

// Async thunk for login
export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }: LoginFormInputs, { rejectWithValue }) => {
    try {
      const data = await loginService({ email, password });
      return data?.data;
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "Login failed");
      return rejectWithValue(error.response?.data?.message ?? "Login failed");
    }
  }
);

// Async thunk for signup
export const signup = createAsyncThunk(
  "auth/signup",
  async (signUpData: any, { rejectWithValue }) => {
    try {
      const data = await signupService(signUpData);
      toast.success("Signup successful");
      return data.user; // Adjust based on API response structure
    } catch (error: any) {
      toast.error(error.response?.data?.message ?? "Signup failed");
      return rejectWithValue(error.response?.data?.message ?? "Signup failed");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.authenticatingUser = false;
      state.error = null;
      localStorage.clear(); // Remove from localStorage on logout
    },
    updateTokens: (state, action: PayloadAction<Tokens>) => {
      if (state.user) {
        state.user.access_token = action.payload.access_token;
        state.user.refreshToken = action.payload.refreshToken;
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    updateUnAuthorizedForgotPassword: (state, action: PayloadAction<any>) => {
      state.unAuthorizedForgotPassword = {
        ...state.unAuthorizedForgotPassword,
        [action.payload.name]: action.payload.value,
      };
    },
    resetUnAuthorizedForgotPassword: (state) => {
      state.unAuthorizedForgotPassword = {
        email: "",
        hash: "",
        otp: "",
      };
    },
  },
  extraReducers: (builder) => {
    // Login reducers
    builder
      .addCase(login.pending, (state) => {
        state.authenticatingUser = true;
        state.error = null;
      })
      .addCase(
        login.fulfilled,
        (state, action: PayloadAction<{ access_token: string; refreshToken: string; user: User }>) => {
          state.authenticatingUser = false;
          const { user }: any = action.payload;
          if (acceptedRoles.includes(user.roles)) {
            toast.success("Login successful");
            state.user = action.payload;
            state.isAuthenticated = true;
            localStorage.setItem("user", JSON.stringify(action.payload));
          } else {
            toast.error("You are not authorized to access this application");
          }
        }
      )
      .addCase(login.rejected, (state, action: PayloadAction<any>) => {
        state.authenticatingUser = false;
        state.error = action.payload;
      });

    // Signup reducers
    builder
      .addCase(signup.pending, (state) => {
        state.authenticatingUser = true;
        state.error = null;
      })
      .addCase(
        signup.fulfilled,
        (state, action: PayloadAction<{ access_token: string; refreshToken: string; user: User }>) => {
          state.authenticatingUser = false;
          state.user = action.payload;
          state.isAuthenticated = true;
          localStorage.setItem("user", JSON.stringify(action.payload)); // Save to localStorage
        }
      )
      .addCase(signup.rejected, (state, action: PayloadAction<any>) => {
        state.authenticatingUser = false;
        state.error = action.payload;
      });
  },
});

export const {
  logout,
  updateTokens,
  updateUnAuthorizedForgotPassword,
  resetUnAuthorizedForgotPassword,
} = authSlice.actions;
export default authSlice.reducer;
